/**
 * K8s Health Console
 *
 * Pod-level visibility for the DigitalOcean Managed K8s cluster.
 * Polls GET /admin-services/k8s every 15 seconds.
 *
 * Sections (all preserved from source):
 *   - Cluster KPIs (name/region/version, nodes ready, pods running/failing, restarts)
 *   - "Not connected" empty state when endpoint returns null
 *   - Deployments table (name, image, replicas, available, restarts, status)
 *   - Horizontal Pod Autoscalers (min/max/current replicas, CPU bar)
 *   - Pods table (name, namespace, node, CPU%, mem MB, restarts, age, status)
 *   - Recent K8s events (Warning vs Normal, reason, object, message, age)
 */
import { Cpu, ExternalLink, RefreshCw, AlertTriangle, CheckCircle } from 'lucide-react';
import { useK8sHealth } from '@/hooks/useK8sHealth';
import { Card, Skeleton, StatCard } from '@/components/ui/atoms';
import { fmtNum } from '@/lib/format';
import type { K8sEvent, K8sHpa, K8sPod, K8sDeployment } from '@/api/k8sHealth';

// ─── Status helpers ───────────────────────────────────────────────────────

type PodLevel = 'healthy' | 'warn' | 'down' | 'pending' | 'unknown';

function podLevel(status: string): PodLevel {
  switch (String(status).toLowerCase()) {
    case 'running':
      return 'healthy';
    case 'pending':
      return 'warn';
    case 'failed':
    case 'crashloopbackoff':
    case 'evicted':
    case 'error':
      return 'down';
    case 'succeeded':
      return 'pending';
    default:
      return 'unknown';
  }
}

const POD_STATUS_BADGE: Record<PodLevel, string> = {
  healthy: 'bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-500/30',
  warn: 'bg-amber-500/20 text-amber-300 ring-1 ring-amber-500/30',
  down: 'bg-red-500/20 text-red-300 ring-1 ring-red-500/30',
  pending: 'bg-sky-500/20 text-sky-300 ring-1 ring-sky-500/30',
  unknown: 'bg-white/10 text-nurev-muted ring-1 ring-white/10'
};

function PodStatusBadge({ status }: { status: string }) {
  const level = podLevel(status);
  const cls = POD_STATUS_BADGE[level];
  return (
    <span className={`inline-block rounded px-2 py-0.5 text-[10px] font-medium ${cls}`}>
      {status}
    </span>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────

function DeploymentRow({ d }: { d: K8sDeployment }) {
  const ok = d.available === d.replicas;
  const level: PodLevel = ok ? 'healthy' : 'warn';
  return (
    <tr className="border-b border-nurev-border last:border-b-0 hover:bg-white/[0.02]">
      <td className="px-5 py-2.5 text-nurev-text font-bold text-xs">{d.name}</td>
      <td
        className="px-5 py-2.5 text-nurev-muted font-mono text-[10.5px] truncate max-w-[200px]"
        title={d.image}
      >
        {d.image || '—'}
      </td>
      <td className="px-5 py-2.5 text-right text-nurev-text text-xs">{d.replicas}</td>
      <td
        className={`px-5 py-2.5 text-right font-bold text-xs ${
          ok ? 'text-emerald-300' : 'text-amber-300'
        }`}
      >
        {d.available}
      </td>
      <td
        className={`px-5 py-2.5 text-right font-bold text-xs ${
          (d.restarts || 0) > 5 ? 'text-red-300' : 'text-nurev-muted'
        }`}
      >
        {d.restarts || 0}
      </td>
      <td className="px-5 py-2.5">
        <span
          className={`inline-block rounded px-2 py-0.5 text-[10px] font-medium ${POD_STATUS_BADGE[level]}`}
        >
          {ok ? 'healthy' : 'degraded'}
        </span>
      </td>
    </tr>
  );
}

function PodRow({ p }: { p: K8sPod }) {
  return (
    <tr className="border-b border-nurev-border last:border-b-0">
      <td className="px-5 py-2.5 text-nurev-text font-mono text-[11px]">{p.name}</td>
      <td className="px-5 py-2.5 text-nurev-muted text-xs">{p.namespace}</td>
      <td className="px-5 py-2.5 text-nurev-muted text-[11px]">{p.node || '—'}</td>
      <td className="px-5 py-2.5 text-right text-nurev-text text-xs">
        {p.cpuPct != null ? `${p.cpuPct}%` : '—'}
      </td>
      <td className="px-5 py-2.5 text-right text-nurev-text text-xs">
        {p.memMb != null ? `${p.memMb} MB` : '—'}
      </td>
      <td
        className={`px-5 py-2.5 text-right font-bold text-xs ${
          (p.restarts || 0) > 5 ? 'text-red-300' : 'text-nurev-muted'
        }`}
      >
        {p.restarts || 0}
      </td>
      <td className="px-5 py-2.5 text-right text-nurev-muted text-[11px]">{p.age || '—'}</td>
      <td className="px-5 py-2.5">
        <PodStatusBadge status={p.status} />
      </td>
    </tr>
  );
}

function HpaCard({ h }: { h: K8sHpa }) {
  const usagePct = h.targetCpu && h.currentCpu != null ? (h.currentCpu / h.targetCpu) * 100 : 0;
  return (
    <div className="bg-white/[0.02] border border-nurev-border rounded-xl p-3">
      <div className="text-xs font-bold text-nurev-text mb-1">{h.deployment}</div>
      <div className="text-[10px] text-nurev-muted mb-2">
        Min {h.min} · Max {h.max}
      </div>
      <div className="text-2xl font-bold text-nurev-text">
        {h.current}{' '}
        <span className="text-sm text-nurev-muted font-medium">replicas</span>
      </div>
      {h.currentCpu != null && (
        <div className="mt-2">
          <div className="text-[10px] text-nurev-muted mb-1">
            CPU {h.currentCpu}% / {h.targetCpu}%
          </div>
          <div className="h-1.5 rounded-full bg-white/[0.04] overflow-hidden">
            <div
              className="h-full rounded-full bg-amber-500"
              style={{ width: `${Math.min(100, usagePct)}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function EventItem({ e }: { e: K8sEvent }) {
  const isWarning = e.type === 'Warning';
  return (
    <li
      className={`flex items-start gap-3 px-3 py-2 rounded-lg ${
        isWarning
          ? 'bg-amber-500/[0.08] border border-amber-500/20'
          : 'bg-white/[0.02] border border-nurev-border'
      }`}
    >
      {isWarning ? (
        <AlertTriangle size={12} className="text-amber-300 flex-shrink-0 mt-0.5" />
      ) : (
        <CheckCircle size={12} className="text-emerald-300 flex-shrink-0 mt-0.5" />
      )}
      <div className="flex-1 min-w-0">
        <div className="text-xs font-bold text-nurev-text">
          {e.reason}{' '}
          <span className="text-nurev-muted font-normal">· {e.object}</span>
        </div>
        <div className="text-[11px] text-nurev-muted truncate">{e.message}</div>
      </div>
      <div className="text-[10px] text-nurev-muted whitespace-nowrap">{e.age}</div>
    </li>
  );
}

// ─── Main screen ──────────────────────────────────────────────────────────

export function K8sHealthScreen() {
  const { data, isLoading, isFetching, dataUpdatedAt, refetch } = useK8sHealth();

  const cluster = data?.cluster ?? {};
  const deployments = data?.deployments ?? [];
  const pods = data?.pods ?? [];
  const events = data?.events ?? [];
  const hpa = data?.hpa ?? [];

  const runningPods = pods.filter(p => podLevel(p.status) === 'healthy').length;
  const failingPods = pods.filter(p => podLevel(p.status) === 'down').length;
  const totalRestarts = pods.reduce((s, p) => s + (p.restarts || 0), 0);

  const lastUpdated = dataUpdatedAt
    ? new Date(dataUpdatedAt).toLocaleTimeString()
    : null;

  return (
    <div className="mx-auto max-w-7xl space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl lg:text-2xl font-bold text-nurev-text">
            Kubernetes Health
          </h1>
          <p className="text-xs text-nurev-muted mt-1">
            DigitalOcean Managed K8s · polls every 15 seconds.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {lastUpdated && (
            <span className="text-[10px] text-nurev-muted">
              updated {lastUpdated}
            </span>
          )}
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/[0.04] border border-nurev-border text-xs font-bold text-nurev-text hover:bg-white/[0.07] transition-colors disabled:opacity-50 cursor-pointer"
          >
            <RefreshCw
              size={12}
              className={isFetching ? 'animate-spin' : ''}
            />
            Refresh
          </button>
        </div>
      </div>

      {/* Cluster KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Card className="p-4">
          <p className="text-[10px] uppercase tracking-wider text-nurev-muted font-bold mb-1">
            Cluster
          </p>
          {isLoading ? (
            <Skeleton className="h-6 w-28" />
          ) : (
            <>
              <p className="text-base font-bold text-nurev-text truncate">
                {cluster.name || '—'}
              </p>
              <p className="text-[10px] text-nurev-muted mt-0.5">
                {cluster.region || ''} · {cluster.version || ''}
              </p>
            </>
          )}
        </Card>

        <StatCard
          label="Nodes ready"
          value={
            isLoading
              ? '—'
              : `${fmtNum(cluster.nodeReady ?? 0)} / ${cluster.nodeCount ?? 0}`
          }
          color="text-emerald-300"
          loading={isLoading}
        />

        <StatCard
          label="Pods running"
          value={isLoading ? '—' : `${fmtNum(runningPods)} / ${pods.length}`}
          color="text-emerald-300"
          loading={isLoading}
        />

        {/* Pods failing — StatCard doesn't take className; wrap in a div for the alert border */}
        <div className={failingPods > 0 ? 'ring-1 ring-red-500/40 rounded-xl' : ''}>
          <StatCard
            label="Pods failing"
            value={isLoading ? '—' : fmtNum(failingPods)}
            color={failingPods > 0 ? 'text-red-300' : 'text-nurev-text'}
            loading={isLoading}
          />
        </div>

        <StatCard
          label="Total restarts"
          value={isLoading ? '—' : fmtNum(totalRestarts)}
          sub="last 24h"
          loading={isLoading}
        />
      </div>

      {/* Not-connected empty state — shown when endpoint returns null (404 / NOT_CONFIGURED) */}
      {!isLoading && data === null && (
        <Card className="p-8 text-center">
          <Cpu size={28} className="mx-auto mb-3 text-nurev-muted" />
          <p className="text-sm font-bold text-nurev-text mb-1">
            K8s monitoring not connected
          </p>
          <p className="text-xs text-nurev-muted max-w-md mx-auto">
            Backend endpoint{' '}
            <code className="px-1 py-0.5 rounded bg-white/[0.04] text-amber-300 font-mono text-[10px]">
              /admin-services/k8s
            </code>{' '}
            needs to be wired to your DigitalOcean K8s API. Once connected,
            this page shows live pod state, recent events, and HPA scaling.
          </p>
          <a
            href="https://cloud.digitalocean.com/kubernetes/clusters"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/[0.04] border border-nurev-border text-xs font-bold text-nurev-text hover:bg-white/[0.07] transition-colors no-underline"
          >
            DigitalOcean cluster console <ExternalLink size={11} />
          </a>
        </Card>
      )}

      {/* Deployments table */}
      {deployments.length > 0 && (
        <div className="bg-nurev-surface border border-nurev-border rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-nurev-border">
            <h3 className="text-sm font-bold text-nurev-text">Deployments</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-nurev-border text-nurev-muted">
                  <th className="px-5 py-2.5 text-left font-medium uppercase text-[10px] tracking-wider">
                    Name
                  </th>
                  <th className="px-5 py-2.5 text-left font-medium uppercase text-[10px] tracking-wider">
                    Image
                  </th>
                  <th className="px-5 py-2.5 text-right font-medium uppercase text-[10px] tracking-wider">
                    Replicas
                  </th>
                  <th className="px-5 py-2.5 text-right font-medium uppercase text-[10px] tracking-wider">
                    Available
                  </th>
                  <th className="px-5 py-2.5 text-right font-medium uppercase text-[10px] tracking-wider">
                    Restarts
                  </th>
                  <th className="px-5 py-2.5 text-left font-medium uppercase text-[10px] tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {deployments.map(d => (
                  <DeploymentRow key={d.name} d={d} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Horizontal Pod Autoscalers */}
      {hpa.length > 0 && (
        <Card>
          <h3 className="text-sm font-bold text-nurev-text mb-3">
            Horizontal pod autoscalers
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {hpa.map(h => (
              <HpaCard key={h.deployment} h={h} />
            ))}
          </div>
        </Card>
      )}

      {/* Pods table */}
      {pods.length > 0 && (
        <div className="bg-nurev-surface border border-nurev-border rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-nurev-border">
            <h3 className="text-sm font-bold text-nurev-text">Pods</h3>
          </div>
          <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-nurev-surface">
                <tr className="border-b border-nurev-border text-nurev-muted">
                  <th className="px-5 py-2.5 text-left font-medium uppercase text-[10px] tracking-wider">
                    Pod
                  </th>
                  <th className="px-5 py-2.5 text-left font-medium uppercase text-[10px] tracking-wider">
                    Namespace
                  </th>
                  <th className="px-5 py-2.5 text-left font-medium uppercase text-[10px] tracking-wider">
                    Node
                  </th>
                  <th className="px-5 py-2.5 text-right font-medium uppercase text-[10px] tracking-wider">
                    CPU
                  </th>
                  <th className="px-5 py-2.5 text-right font-medium uppercase text-[10px] tracking-wider">
                    Mem
                  </th>
                  <th className="px-5 py-2.5 text-right font-medium uppercase text-[10px] tracking-wider">
                    Restarts
                  </th>
                  <th className="px-5 py-2.5 text-right font-medium uppercase text-[10px] tracking-wider">
                    Age
                  </th>
                  <th className="px-5 py-2.5 text-left font-medium uppercase text-[10px] tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {pods.map(p => (
                  <PodRow key={p.name} p={p} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recent K8s events */}
      {events.length > 0 && (
        <Card>
          <h3 className="text-sm font-bold text-nurev-text mb-3">
            Recent K8s events
          </h3>
          <ul className="space-y-1.5 max-h-[300px] overflow-y-auto">
            {events.map((e, i) => (
              <EventItem key={i} e={e} />
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}
